/**
 * Python code generator using the Perceptron SDK
 */

import { Annotations, BoundingBox, Point } from "../../apiContracts/annotation";
import { THINKING_REASONING_EFFORT } from "../../apiContracts/chatCompletions";
import { DEMO_MAX_TOKENS } from "../../constants";
import { ContentItem, ExportCodeConfig, OutputFormat } from "./types";

function mapOutputFormatToExpects(outputFormat: OutputFormat): string | null {
  switch (outputFormat) {
    case "POINT":
    case "BOX":
    case "POLYGON":
    case "CLIP":
      return outputFormat.toLowerCase();
    default:
      return null;
  }
}

function getResultAttrForExpects(expects: string | null): string | null {
  switch (expects) {
    case "point":
      return "points";
    case "box":
      return "boxes";
    case "polygon":
      return "polygons";
    case "clip":
      return "clips";
    default:
      return null;
  }
}

function generatePointCode(point: Point, varName: string): string {
  return `point(${point.x}, ${point.y}, image=${varName}, mention="${point.label}")`;
}

function generateBoxCode(box: BoundingBox, varName: string): string {
  // SDK signature: box(x1, y1, x2, y2, *, image=None, mention=None, t=None).
  // Convert (top-left + width/height) to (top-left + bottom-right).
  const x2 = box.x + box.width;
  const y2 = box.y + box.height;
  return `box(${box.x}, ${box.y}, ${x2}, ${y2}, image=${varName}, mention="${box.label}")`;
}

interface ProcessedContent {
  variableAssignments: string[];
  returnParts: string[];
  hasPoint: boolean;
  hasBox: boolean;
}

function processAnnotations(
  annotations: Annotations | undefined,
  varName: string,
  returnParts: string[],
): { hasPoint: boolean; hasBox: boolean } {
  let hasPoint = false;
  let hasBox = false;

  if (annotations) {
    for (const point of annotations.points) {
      returnParts.push(generatePointCode(point, varName));
      hasPoint = true;
    }
    for (const box of annotations.boundingBoxes) {
      returnParts.push(generateBoxCode(box, varName));
      hasBox = true;
    }
  }

  return { hasPoint, hasBox };
}

/**
 * Process content items into variable assignments and return parts.
 */
function processContentItems(
  items: ContentItem[],
  isSystem: boolean,
  imageStartIndex: number,
  videoStartIndex: number,
  audioStartIndex: number,
): ProcessedContent {
  const variableAssignments: string[] = [];
  const returnParts: string[] = [];
  let hasPoint = false;
  let hasBox = false;

  let imageCount = imageStartIndex;
  let videoCount = videoStartIndex;
  let audioCount = audioStartIndex;

  for (const item of items) {
    switch (item.type) {
      case "image": {
        imageCount++;
        const varName = `image_${imageCount}`;
        variableAssignments.push(`${varName} = image("${item.url}")`);
        returnParts.push(varName);
        const result = processAnnotations(item.annotations, varName, returnParts);
        hasPoint = hasPoint || result.hasPoint;
        hasBox = hasBox || result.hasBox;
        break;
      }
      case "video": {
        videoCount++;
        const varName = `video_${videoCount}`;
        variableAssignments.push(`${varName} = video("${item.url}")`);
        returnParts.push(varName);
        const result = processAnnotations(item.annotations, varName, returnParts);
        hasPoint = hasPoint || result.hasPoint;
        hasBox = hasBox || result.hasBox;
        break;
      }
      case "audio": {
        audioCount++;
        const varName = `audio_${audioCount}`;
        variableAssignments.push(`${varName} = audio("${item.url}")`);
        returnParts.push(varName);
        break;
      }
      case "text": {
        // JSON.stringify escapes special chars (\n, \t, \r, ", \, etc.) and wraps in quotes.
        const escapedText = JSON.stringify(item.text);
        returnParts.push(isSystem ? `system(${escapedText})` : `text(${escapedText})`);
        break;
      }
    }
  }

  return { variableAssignments, returnParts, hasPoint, hasBox };
}

export function generatePython(config: ExportCodeConfig): string {
  const allVariableAssignments: string[] = [];
  const allReturnParts: string[] = [];
  let hasPointAnnotation = false;
  let hasBoxAnnotation = false;

  const systemImages = (config.systemContent || []).filter((c) => c.type === "image").length;
  const systemVideos = (config.systemContent || []).filter((c) => c.type === "video").length;
  const systemAudios = (config.systemContent || []).filter((c) => c.type === "audio").length;

  if (config.systemContent && config.systemContent.length > 0) {
    const result = processContentItems(config.systemContent, true, 0, 0, 0);
    allVariableAssignments.push(...result.variableAssignments);
    allReturnParts.push(...result.returnParts);
    hasPointAnnotation = hasPointAnnotation || result.hasPoint;
    hasBoxAnnotation = hasBoxAnnotation || result.hasBox;
  }

  const userResult = processContentItems(config.userContent, false, systemImages, systemVideos, systemAudios);
  allVariableAssignments.push(...userResult.variableAssignments);
  allReturnParts.push(...userResult.returnParts);
  hasPointAnnotation = hasPointAnnotation || userResult.hasPoint;
  hasBoxAnnotation = hasBoxAnnotation || userResult.hasBox;

  const allContent = [...(config.systemContent || []), ...config.userContent];

  // Imports: `configure` and `perceive` are always needed.
  const importParts = ["configure", "perceive"];
  const hasSystemText = (config.systemContent || []).some((c) => c.type === "text");
  const hasImage = allContent.some((c) => c.type === "image");
  const hasVideo = allContent.some((c) => c.type === "video");
  const hasAudio = allContent.some((c) => c.type === "audio");
  const hasUserText = config.userContent.some((c) => c.type === "text");

  if (hasSystemText) importParts.push("system");
  if (hasImage) importParts.push("image");
  if (hasVideo) importParts.push("video");
  if (hasAudio) importParts.push("audio");
  if (hasUserText) importParts.push("text");
  if (hasPointAnnotation) importParts.push("point");
  if (hasBoxAnnotation) importParts.push("box");

  const apiKeyValue = config.apiKey || "<your_api_key_here>";

  // @perceive parameters — model lives on configure(), not here.
  const decoratorParams: string[] = [`max_tokens=${DEMO_MAX_TOKENS}`];
  if (config.temperature !== undefined) {
    decoratorParams.push(`temperature=${config.temperature}`);
  }
  const expects = mapOutputFormatToExpects(config.outputFormat);
  if (expects) {
    decoratorParams.push(`expects="${expects}"`);
  }
  if (config.enableReasoning) {
    decoratorParams.push(`reasoning_effort="${THINKING_REASONING_EFFORT}"`);
  }
  if (config.enableAudioInVideo) {
    decoratorParams.push("enable_audio_in_video=True");
  }
  const decoratorStr = `@perceive(${decoratorParams.join(", ")})`;

  // Result printing — always `result.text`. Add the structured-output accessor
  // matching the SDK's per-output-type fields.
  const resultOutputParts = [`print(result.text)`];
  const resultAttr = getResultAttrForExpects(expects);
  if (resultAttr) {
    resultOutputParts.push(`print(result.${resultAttr})`);
  }
  const resultOutput = resultOutputParts.join("\n");

  const variableLines = allVariableAssignments.map((v) => `    ${v}`).join("\n");
  const returnStatement =
    allReturnParts.length > 0
      ? allReturnParts.length === 1
        ? allReturnParts[0]
        : `(\n        ${allReturnParts.join("\n        + ")}\n    )`
      : 'text("<YOUR_PROMPT>")';

  const functionBody =
    allVariableAssignments.length > 0
      ? `${variableLines}\n    return ${returnStatement}`
      : `    return ${returnStatement}`;

  return `from perceptron import ${importParts.join(", ")}

configure(
    provider="perceptron",
    model="${config.model}",
    api_key="${apiKeyValue}",
)

${decoratorStr}
def run():
${functionBody}

result = run()
${resultOutput}
`;
}
